import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { API_BASE_URL } from '../firebase/config';
import SkeletonLoading from './SkeletonLoading';

const ProfileManager = ({ profileData }) => {
    const defaultForm = {
        email: '',
        firstName: '',
        otherNames: '',
        phone: '',
        posAdmin: false,
        station: '',
        stationId: '',
        uid: profileData?.id || '',
        uname: `${profileData?.firstName || ''} ${profileData?.otherNames || ''}`,
    };

    const [stations, setStations] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [filteredProfiles, setFilteredProfiles] = useState([]);
    const [paginatedProfiles, setPaginatedProfiles] = useState([]);
    const [formData, setFormData] = useState(defaultForm);
    const [editingId, setEditingId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalPages: 1,
    });

    useEffect(() => {
        fetchStations();
        fetchProfiles();
    }, []);

    useEffect(() => {
        paginateProfiles();
    }, [filteredProfiles, pagination.currentPage]);

    useEffect(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = profiles.filter(profile => {
            return (
                profile.firstName?.toLowerCase().includes(lowerSearch) ||
                profile.otherNames?.toLowerCase().includes(lowerSearch) ||
                profile.phone?.toLowerCase().includes(lowerSearch) ||
                profile.station?.toLowerCase().includes(lowerSearch)
            );
        });

        setFilteredProfiles(filtered);

        setPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalPages: Math.ceil(filtered.length / prev.limit) || 1,
        }));
    }, [searchTerm, profiles]);

    const fetchStations = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/dropdown/station/oid?value=07`);
            setStations(response.data.message || []);
        } catch (err) {
            console.error('Failed to fetch stations:', err);
        }
    };

    const fetchProfiles = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/profile/read-all`);
            const data = response.data.data || [];
            setProfiles(data);
            setFilteredProfiles(data);
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
                totalPages: Math.ceil(data.length / prev.limit) || 1,
            }));
        } catch (err) {
            setError('Failed to fetch profiles');
        } finally {
            setIsLoading(false);
        }
    };

    const paginateProfiles = () => {
        const { currentPage, limit } = pagination;
        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        setPaginatedProfiles(filteredProfiles.slice(startIndex, endIndex));
    };

    const handleOpenModal = (profile = null) => {
        if (profile) {
            setEditingId(profile.id);
            setFormData({
                ...defaultForm,
                ...profile,
            });
        } else {
            setEditingId(null);
            setFormData(defaultForm);
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setFormData(defaultForm);
        setEditingId(null);
    };

    const sanitizeProfileData = (formData) => ({
        email: formData.email,
        firstName: formData.firstName,
        otherNames: formData.otherNames,
        phone: formData.phone,
        posAdmin: formData.posAdmin,
        station: formData.station,
        stationId: formData.stationId,
        uid: formData.uid,
        uname: formData.uname,
    });

    const handleSubmit = async () => {
        const payload = sanitizeProfileData(formData);
        console.log(`payload: ${JSON.stringify(payload)}`);
        if (!payload.firstName.trim() || !payload.phone.trim()) {
            alert('Please fill in the required fields: First Name and Phone');
            return;
        }

        setIsSubmitting(true);
        try {

            await axios.post(`${API_BASE_URL}/profile/create`, payload);
            closeModal();
            await fetchProfiles();
        } catch (err) {
            alert('Error submitting form');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetPin = async (phone) => {
        if (!window.confirm('Are you sure you want to reset the PIN for this user?')) return;

        try {
            await axios.post(`${API_BASE_URL}/profile/reset-pin`, { phone });
            alert('PIN has been reset successfully!');
        } catch (error) {
            console.error('Failed to reset PIN:', error);
            alert('Failed to reset PIN');
        }
    };


    const handlePageChange = (direction) => {
        setPagination(prev => {
            let newPage = prev.currentPage;
            if (direction === 'next' && prev.currentPage < prev.totalPages) {
                newPage++;
            } else if (direction === 'prev' && prev.currentPage > 1) {
                newPage--;
            }
            return { ...prev, currentPage: newPage };
        });
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Profile Manager</h2>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 inline-flex items-center"
                >
                    <FaPlus className="mr-2" /> Create Profile
                </button>
            </div>

            {/* Search input */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by name, phone, or station..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full md:w-1/3 p-2 border rounded"
                />
            </div>

            {error && <div className="text-red-600 mb-2">{error}</div>}

            {isLoading ? (
                <SkeletonLoading />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full border text-sm bg-white">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border px-4 py-2 text-left">#</th>
                                <th className="border px-4 py-2 text-left">Name</th>
                                <th className="border px-4 py-2 text-left">Phone</th>
                                <th className="border px-4 py-2 text-left">Station</th>
                                <th className="border px-4 py-2 text-left">Admin</th>
                                <th className="border px-4 py-2 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedProfiles.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center p-4">
                                        No profiles found
                                    </td>
                                </tr>
                            ) : (
                                paginatedProfiles.map((profile, idx) => (
                                    <tr key={profile.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                        <td className="border px-4 py-2">
                                            {(pagination.currentPage - 1) * pagination.limit + idx + 1}
                                        </td>
                                        <td className="border px-4 py-2">
                                            {profile.firstName} {profile.otherNames}
                                        </td>
                                        <td className="border px-4 py-2">{profile.phone}</td>
                                        <td className="border px-4 py-2">{profile.station || '-'}</td>
                                        <td className="border px-4 py-2">{profile.posAdmin ? 'Yes' : 'No'}</td>
                                        <td className="border px-4 py-2 space-x-2">
                                            <button
                                                className="text-blue-600 hover:underline"
                                                onClick={() => handleOpenModal(profile)}
                                            >
                                                <FaEdit className="inline-block mr-1" />
                                                Edit
                                            </button>

                                            <button
                                                className="text-red-600 hover:underline"
                                                onClick={() => handleResetPin(profile.phone)}
                                            >
                                                🔒 Reset PIN
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
                <div>
                    Page {pagination.currentPage} of {pagination.totalPages} | Total: {filteredProfiles.length}
                </div>
                <div className="flex gap-2">
                    <button
                        disabled={pagination.currentPage <= 1}
                        onClick={() => handlePageChange('prev')}
                        className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400 disabled:opacity-50"
                    >
                        <FaAngleLeft />
                    </button>
                    <button
                        disabled={pagination.currentPage >= pagination.totalPages}
                        onClick={() => handlePageChange('next')}
                        className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400 disabled:opacity-50"
                    >
                        <FaAngleRight />
                    </button>
                </div>
            </div>

            {/* Modal */}
            {modalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-title"
                    tabIndex={-1}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') closeModal();
                    }}
                >
                    <div className="bg-white p-6 rounded-md w-full max-w-xl">
                        <h2 id="modal-title" className="text-lg font-bold mb-4">
                            {editingId ? 'Update Profile' : 'Create Profile'}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                className="border p-2 rounded w-full"
                            />
                            <input
                                type="text"
                                placeholder="Other Names"
                                value={formData.otherNames}
                                onChange={e => setFormData({ ...formData, otherNames: e.target.value })}
                                className="border p-2 rounded w-full"
                            />
                            <input
                                type="text"
                                placeholder="Phone"
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                className="border p-2 rounded w-full"
                            />
                            <input
                                type="text"
                                placeholder="Email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="border p-2 rounded w-full"
                            />
                            <select
                                value={formData.stationId}
                                onChange={e => {
                                    const selectedId = e.target.value;
                                    const selectedStation = stations.find(s => s.id === selectedId);
                                    setFormData({
                                        ...formData,
                                        stationId: selectedId,
                                        station: selectedStation ? selectedStation.name : '',
                                    });
                                }}
                                className="border p-2 rounded w-full"
                            >
                                <option value="">Select Station</option>
                                {stations.map(station => (
                                    <option key={station.id} value={station.id}>
                                        {station.name}
                                    </option>
                                ))}
                            </select>
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={formData.posAdmin}
                                    onChange={e => setFormData({ ...formData, posAdmin: e.target.checked })}
                                />
                                <span>POS Admin</span>
                            </label>
                        </div>
                        <div className="mt-4 flex justify-end gap-4">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : (editingId ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileManager;
